import { ModuleId, MODULES } from '../../../content'
import { AppState } from '../../../app/types'
import type { QuestionState, FamilyMasteryState } from '../../../mastery'
import {
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
  Classroom,
} from '../../types'
import { generateAccountId } from '../creation'
import { generateJoinCode } from '../classrooms'
import { StreakState, DailyStreakState } from '../../../app/types'
import { SeededRng } from '../../../utils/deterministicRng'

// ── Fixed seed for deterministic demo data ──────────────────────────────
// Using a fixed seed ensures demo accounts are identical every time,
// making debugging reproducible. Seed 20260312 = project date reference.
export const DEMO_SEED = 20260312

/**
 * Create a demo learner account with realistic progress.
 * Uses deterministic RNG so demo data is always identical.
 */
export function createDemoLearner(
  rng: SeededRng,
  name: string,
  level: number,
  xp: number,
  grade: number,
  progressData: {
    completedQuestions: Record<ModuleId | string, number>
    masteryScores: Record<string, number>
    streak?: StreakState
    dailyStreak?: DailyStreakState
    unlockedAchievements?: string[]
  },
): {
  account: LearnerAccount
  data: AppState
} {
  const now = Date.now()
  const username = name.toLowerCase().replace(/\s+/g, '')
  const account: LearnerAccount = {
    id: generateAccountId(),
    role: 'learner',
    displayName: name,
    username,
    grade,
    createdAt: now - 60 * 24 * 60 * 60 * 1000, // 60 days ago
    lastActive: now,
    isDemo: true,
    linkCode: undefined,
  }

  // Generate question states with realistic, deterministic history
  const questionStates: Record<string, QuestionState> = {}
  const groupScope = `g${grade}`
  Object.entries(progressData.completedQuestions).forEach(
    ([moduleId, count]) => {
      for (let i = 0; i < count; i++) {
        const questionId = `${moduleId}_demo_q${i}`
        const stateKey = `${questionId}_${groupScope}`
        const isCorrect = rng.chance(0.75)
        const daysAgo = rng.int(0, 13)
        const attempts = rng.int(1, 4)
        const correctAttempts = isCorrect
          ? Math.max(1, rng.int(1, attempts))
          : rng.int(0, Math.max(0, attempts - 1))
        questionStates[stateKey] = {
          questionId,
          status: 'practiced',
          attempts,
          correctAttempts,
          lastAttemptDate: now - daysAgo * 24 * 60 * 60 * 1000,
          masteryPoints: rng.int(50, 99),
          history: [
            {
              isCorrect: true,
              timestamp: now - (daysAgo + 3) * 24 * 60 * 60 * 1000,
            },
            {
              isCorrect,
              timestamp: now - daysAgo * 24 * 60 * 60 * 1000,
            },
          ],
        }
      }
    },
  )

  // Generate family mastery records with realistic data
  const familyMastery: Record<string, FamilyMasteryState> = {}
  Object.entries(progressData.masteryScores).forEach(([familyId, score]) => {
    const masteryKey = `${familyId}_${groupScope}`
    const attempts = Math.max(10, Math.floor(score / 4) + rng.int(0, 14))
    familyMastery[masteryKey] = {
      familyId,
      masteryScore: score,
      attempts,
      correct: Math.floor((score / 100) * attempts),
      lastSeen: now - rng.int(0, 6) * 24 * 60 * 60 * 1000,
      confidence: Math.min(1, score / 100 + 0.15),
      history: [
        {
          questionId: `${familyId}_demo_1`,
          isCorrect: true,
          timestamp: now - 10 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 20) + 1),
        },
        {
          questionId: `${familyId}_demo_2`,
          isCorrect: score > 50,
          timestamp: now - 5 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 20) + 2),
        },
        {
          questionId: `${familyId}_demo_3`,
          isCorrect: score > 70,
          timestamp: now - 2 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 15) + 1),
        },
      ],
    }
  })

  // Build progress from MODULES registry (data-driven, not hard-coded)
  const progress = Object.fromEntries(
    MODULES.map((m) => [
      m.id,
      { cheatSheetViewed: (progressData.completedQuestions[m.id] ?? 0) > 10 },
    ]),
  ) as AppState['progress']

  const data: AppState = {
    stats: {
      name,
      xp,
      level,
      unlockedAchievements: progressData.unlockedAchievements,
    },
    progress,
    questionStates,
    familyMastery,
    placementResults: {},
    pendingLevelUp: undefined,
    streak: progressData.streak,
    dailyStreak: progressData.dailyStreak,
  }
  return { account, data }
}

/**
 * Create demo parent account
 */
export function createDemoParent(name: string, email?: string): ParentAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'parent',
    displayName: name,
    email,
    createdAt: now - 60 * 24 * 60 * 60 * 1000,
    lastActive: now,
    isDemo: true,
  }
}

/**
 * Create demo teacher account.
 * Classrooms are created separately via createDemoClassroom() and stored in top-level registry.
 */
export function createDemoTeacher(
  name: string,
  schoolName: string,
  email?: string,
): TeacherAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'teacher',
    displayName: name,
    email,
    createdAt: now - 90 * 24 * 60 * 60 * 1000,
    lastActive: now,
    schoolName,
    isDemo: true,
  }
}

/**
 * Create demo admin account
 */
export function createDemoAdmin(name: string, email?: string): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: name,
    email,
    createdAt: now - 120 * 24 * 60 * 60 * 1000,
    lastActive: now,
    isDemo: true,
  }
}

/**
 * Create a Classroom object for a demo teacher.
 * Returns the Classroom to be stored in the top-level registry.
 */
export function createDemoClassroom(
  teacher: TeacherAccount,
  classroomName: string,
  groupLevel: number,
  learnerIds: string[],
): Classroom {
  return {
    id: `demo_class_${teacher.id.slice(-6)}`,
    name: classroomName,
    groupLevel,
    learnerIds,
    createdAt: teacher.createdAt,
    teacherId: teacher.id,
    joinCode: generateJoinCode(),
  }
}
